import { useEffect, useMemo, useState } from "react";
import api, { inr, formatDate, BACKEND_URL } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import StatusPill from "@/components/StatusPill";
import { XCircle, Send, Download, Search } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { k: "",           l: "All roles" },
  { k: "customer",   l: "Customer" },
  { k: "distributor",l: "IBA" },
  { k: "franchise",  l: "Franchise" },
];

const STATUSES = [
  { k: "",          l: "All" },
  { k: "delivered", l: "Delivered" },
  { k: "shipped",   l: "Shipped" },
  { k: "processing",l: "Processing" },
  { k: "cancelled", l: "Cancelled" },
];

export default function AdminOrders() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return rows.filter((o) => {
      if (role && o.user_role !== role) return false;
      if (status && (o.status || "").toLowerCase() !== status) return false;
      if (!qq) return true;
      return (
        (o.order_no || "").toLowerCase().includes(qq) ||
        (o.user_code || "").toLowerCase().includes(qq)
      );
    });
  }, [rows, q, role, status]);

  const cancel = async (o) => {
    if (o.status === "cancelled") return;
    const reason = window.prompt(`Cancel order ${o.order_no}? Enter a reason (audit-logged):`, "");
    if (reason === null) return;
    setBusy(o.id);
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/cancel`, { reason });
      toast.success(`Cancelled ${o.order_no}${data.reversed_bv ? ` · SP −${data.reversed_bv}` : ""}${data.refunded_to_wallet ? " · wallet refunded" : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(null); }
  };

  const resend = async (o) => {
    setBusy(o.id);
    try {
      await api.post(`/admin/orders/${o.id}/resend-invoice`);
      // Download the PDF right away so the admin can forward it
      const token = localStorage.getItem("samara_token");
      const res = await fetch(`${BACKEND_URL}/api/invoices/order/${o.order_no}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `invoice-${o.order_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Invoice ${o.order_no} downloaded — audit-logged`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally { setBusy(null); }
  };

  return (
    <DashboardLayout title="All Orders" subtitle={`${visible.length} of ${rows.length} orders`}>
      {/* Filters */}
      <div className="bg-white border border-border p-4 rounded-sm mb-4 flex flex-col md:flex-row gap-3" data-testid="orders-filters">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            data-testid="orders-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search order # or user code…"
            className="w-full border border-border pl-9 pr-3 py-2 text-sm rounded-sm focus:border-royal outline-none"
          />
        </div>
        <select data-testid="orders-role" value={role} onChange={(e) => setRole(e.target.value)}
                className="border border-border px-3 py-2 text-sm rounded-sm focus:border-royal outline-none">
          {ROLES.map((r) => <option key={r.k} value={r.k}>{r.l}</option>)}
        </select>
        <select data-testid="orders-status" value={status} onChange={(e) => setStatus(e.target.value)}
                className="border border-border px-3 py-2 text-sm rounded-sm focus:border-royal outline-none">
          {STATUSES.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
        </select>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-navy text-white">
            <tr>{["Order #", "User", "Role", "Items", "SP", "Total", "Payment", "Status", "Date", ""].map((h) => (
              <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-navy/50">No orders match these filters.</td></tr>
            )}
            {visible.map((o) => {
              const isCancelled = o.status === "cancelled";
              return (
                <tr key={o.id} data-testid={`order-${o.order_no}`}
                    className={`border-b border-border last:border-0 hover:bg-cream/40 ${isCancelled ? "text-navy/40" : ""}`}>
                  <td className={`px-4 py-3 font-medium ${isCancelled ? "line-through text-navy/40" : "text-navy"}`}>{o.order_no}</td>
                  <td className="px-4 py-3">{o.user_code}</td>
                  <td className="px-4 py-3 capitalize">{o.user_role}</td>
                  <td className="px-4 py-3">{o.items?.length || 0}</td>
                  <td className="px-4 py-3 text-gold-deep">{o.total_sp}</td>
                  <td className={`px-4 py-3 font-semibold ${isCancelled ? "line-through text-navy/40" : "text-navy"}`}>{inr(o.total)}</td>
                  <td className="px-4 py-3 capitalize">{o.payment_mode}</td>
                  <td className="px-4 py-3"><StatusPill status={o.status} /></td>
                  <td className="px-4 py-3 text-xs">{formatDate(o.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <button
                        type="button"
                        onClick={() => resend(o)}
                        disabled={busy === o.id}
                        data-testid={`resend-invoice-${o.order_no}`}
                        title="Regenerate & download invoice PDF (audit-logged)"
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border border-royal/30 text-royal hover:bg-royal/5 rounded-sm disabled:opacity-40"
                      >
                        <Send size={10} /> Resend
                      </button>
                      {!isCancelled && (
                        <button
                          type="button"
                          onClick={() => cancel(o)}
                          disabled={busy === o.id}
                          data-testid={`cancel-order-${o.order_no}`}
                          title="Cancel order (reverses SP & refunds wallet payment)"
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border border-rose/40 text-rose hover:bg-rose/10 rounded-sm disabled:opacity-40"
                        >
                          <XCircle size={10} /> Cancel
                        </button>
                      )}
                    </div>
                    {(o.resend_count > 0) && (
                      <div className="mt-1 text-[9px] text-navy/40" data-testid={`resend-count-${o.order_no}`}>
                        Resent {o.resend_count}×
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
